/**
 * Confirmation modal for force-reprocessing a fully-processed vehicle.
 * Shown by VehicleDetailPage when every photo already carries `processed`
 * status — a normal process run would skip them all (the worker's
 * crash-retry guard), so the operator confirms a full re-run with the
 * current pipeline instead. Confirm sends force=true; cancel does nothing.
 *
 * Modal chrome matches components/wholesale-auction/AuctionRowAlertModal.tsx:46-70
 * (overlay + panel + header); primary button matches its navy button
 * (lines 340-353); secondary button matches VehicleDetailPage's
 * secondaryButtonStyle.
 */
import React, { useEffect } from "react"
import { X } from "lucide-react"

import { THEME } from "../../config/theme"

interface Props {
  photoCount: number
  onConfirm: () => void
  onCancel: () => void
}

export default function ReprocessConfirmModal({ photoCount, onConfirm, onCancel }: Props) {
  // Esc-to-close — UX expectation for any modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onCancel])

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        fontFamily: THEME.fonts.primary,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reprocess-confirm-title"
        style={{
          background: THEME.colors.neutral.light.surface,
          borderRadius: THEME.borderRadius.md,
          padding: THEME.spacing.lg,
          minWidth: 0,
          width: "min(480px, 94vw)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: THEME.spacing.md,
          }}
        >
          <div
            id="reprocess-confirm-title"
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: THEME.colors.neutral.light.textPrimary,
            }}
          >
            Re-run All Photos
          </div>
          <button
            type="button"
            onClick={onCancel}
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "none",
              border: "none",
              color: THEME.colors.neutral.light.textSecondary,
              cursor: "pointer",
              padding: 4,
            }}
            aria-label="Close"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div
          style={{
            fontSize: 13,
            color: THEME.colors.neutral.light.textSecondary,
            lineHeight: 1.5,
            marginBottom: THEME.spacing.lg,
          }}
        >
          All {photoCount.toLocaleString()} photo{photoCount === 1 ? " is" : "s are"} already
          processed. Re-running builds fresh versions from the originals. The
          dealer site updates only on the next push.
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: THEME.spacing.sm,
          }}
        >
          <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} style={primaryButtonStyle}>
            Re-run All Photos
          </button>
        </div>
      </div>
    </div>
  )
}

const primaryButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  fontFamily: THEME.fonts.primary,
  color: THEME.colors.white,
  background: THEME.colors.navy,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
}

const secondaryButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
}
